import type { AccountSupabaseClient } from '~/services/account'
import {
  requestEmailOtp,
  verifyEmailOtp,
  isSeedAccount as checkSeedAccount,
  setupAccountKey,
  unlockAccount as unlockAccountService,
  rewrapDekForConversion,
  requestAccountEmailChange,
  isEmailChangeConfirmed,
  finalizeAccountConversion
} from '~/services/account'
import { clearPassphraseKey } from '~/utils/passphrase-cache'

/**
 * Composable for anonymous authentication via seed phrase with key wrapping.
 *
 * Authentication flow:
 * 1. The user provides a 12-word BIP39 seed phrase.
 * 2. `useSeed.deriveCredentials` deterministically produces an anonymous email,
 *    a password, a legacy encryption key, and an AES-KW wrapper key from the seed.
 * 3. The composable attempts to sign in with the derived credentials.
 *    If the account doesn't exist yet, it automatically signs up instead.
 * 4. For new accounts: a random encryption key is generated, wrapped with the
 *    wrapper key, and stored on the server.
 * 5. For existing accounts: the wrapped key is downloaded from the server and
 *    unwrapped with the wrapper key.
 * 6. For legacy accounts (no wrapped key): the account is transparently migrated
 *    by re-encrypting all data with a new random key.
 *
 * No personal data (name, real email, etc.) is ever collected or transmitted.
 */
export function useAuth() {
  const supabase = useSupabaseClient()
  const accountClient = supabase as unknown as AccountSupabaseClient
  const user = useSupabaseUser()

  /**
   * Authenticates using a seed phrase.
   * Derives credentials and wrapper key from the seed, handles sign-in/sign-up,
   * and sets up the encryption key via key wrapping (or legacy migration).
   *
   * @returns `isNewUser` indicates whether a new account was created.
   */
  async function authenticate(
    seed: string,
    allowSignUp = true
  ): Promise<{
    isNewUser: boolean
    error: unknown
  }> {
    const { deriveCredentials } = useSeed()
    const { storeKeyInSession, storeLegacyKey } = useEncryption()
    const {
      setupNewAccountKey,
      retrieveEncryptionKey,
      migrateLegacyAccount,
      hasWrappedKeyOnServer
    } = useKeyWrapping()

    const normalizedSeed = seed.trim().toLowerCase().replace(/\s+/g, ' ')

    const { email, password, encryptionKey: legacyKey, wrapperKey } =
      await deriveCredentials(normalizedSeed)

    // Persist legacy key so sync can use it as fallback for data
    // still encrypted with the old seed-derived key (incomplete migration).
    await storeLegacyKey(legacyKey)

    // Try sign-in first
    const { error: signInError } = await supabase.auth.signInWithPassword({
      email,
      password
    })

    if (!signInError) {
      // Existing user — resolve encryption key
      try {
        const realKey = await resolveExistingUserKey(
          wrapperKey,
          legacyKey,
          hasWrappedKeyOnServer,
          retrieveEncryptionKey,
          migrateLegacyAccount
        )
        await storeKeyInSession(realKey)
        console.log('[auth] Encryption key resolved via key wrapping')
      } catch (e) {
        // Migration or key unwrap failed — fall back to legacy key so the
        // user can still log in. Migration will be retried on next login
        // once the server-side `user_keys` table is available.
        console.warn('[auth] Key resolution failed, using legacy key:', e)
        await storeKeyInSession(legacyKey)
      }
      return { isNewUser: false, error: null }
    }

    // If invalid credentials, treat as new user only when sign-up is allowed.
    // When allowSignUp is false (login tab), a wrong phrase is an error.
    const errorMessage =
      (signInError as { message?: string }).message?.toLowerCase() || ''

    if (
      errorMessage.includes('invalid login credentials') ||
      errorMessage.includes('invalid credentials')
    ) {
      if (!allowSignUp) {
        return { isNewUser: false, error: new Error('invalid_phrase') }
      }

      const { error: signUpError } = await supabase.auth.signUp({
        email,
        password
      })

      if (signUpError) {
        return { isNewUser: false, error: signUpError }
      }

      // New user: generate random key, wrap it, upload
      try {
        const realKey = await setupNewAccountKey(wrapperKey)
        await storeKeyInSession(realKey)
        console.log('[auth] New account key generated and wrapped')
      } catch (e) {
        // Wrapped key upload failed (e.g. user_keys table missing) —
        // fall back to legacy key so the account can still be created.
        console.warn('[auth] Key setup failed, using legacy key:', e)
        await storeKeyInSession(legacyKey)
      }
      return { isNewUser: true, error: null }
    }

    // Other errors (network, rate limit, etc.)
    return { isNewUser: false, error: signInError }
  }

  /**
   * Resolves the encryption key for an existing user.
   * Checks if a wrapped key exists on the server. If yes, unwraps it.
   * If no, performs a legacy migration.
   */
  async function resolveExistingUserKey(
    wrapperKey: CryptoKey,
    legacyKey: CryptoKey,
    hasWrappedKeyOnServer: () => Promise<boolean>,
    retrieveEncryptionKey: (wk: CryptoKey) => Promise<CryptoKey | null>,
    migrateLegacyAccount: (lk: CryptoKey, wk: CryptoKey) => Promise<CryptoKey>
  ): Promise<CryptoKey> {
    const hasWrapped = await hasWrappedKeyOnServer()

    if (hasWrapped) {
      console.log('[auth] Wrapped key found on server, unwrapping')
      const realKey = await retrieveEncryptionKey(wrapperKey)
      if (!realKey) throw new Error('key_unwrap_failed')
      return realKey
    }

    // Legacy account — migrate transparently
    console.log('[auth] No wrapped key on server, migrating legacy account')
    return migrateLegacyAccount(legacyKey, wrapperKey)
  }

  async function requireUserId(): Promise<string> {
    const { data } = await supabase.auth.getUser()
    const id = data?.user?.id
    if (!id) throw new Error('No authenticated user')
    return id
  }

  /**
   * Sends the email OTP. Supabase creates the account if it does not exist,
   * so a single form covers both sign-in and sign-up.
   */
  async function requestOtp(email: string) {
    return requestEmailOtp(accountClient, email, true)
  }

  /**
   * Verifies the emailed OTP and creates the session. The caller must then
   * set up the account key (new user) or unlock it (returning user).
   */
  async function verifyOtp(email: string, token: string) {
    return verifyEmailOtp(accountClient, email, token)
  }

  /**
   * Whether the signed-in account still uses the seed auth model and must be
   * converted before the seed path can be removed.
   */
  async function isSeedAccount() {
    return checkSeedAccount(accountClient)
  }

  /**
   * Creates the DEK for a freshly authenticated account and stores it
   * passphrase-wrapped on the server. Returns the passphrase-wrapped DEK.
   */
  async function setupNewAccount(passphrase: string) {
    const { storeKeyInSession } = useEncryption()
    const dek = await setupAccountKey(
      accountClient,
      await requireUserId(),
      passphrase
    )
    await storeKeyInSession(dek)
    return dek
  }

  /**
   * Unlocks the account DEK with the passphrase or the cached device key.
   */
  async function unlockAccount(passphrase?: string) {
    const { storeKeyInSession } = useEncryption()
    const dek = await unlockAccountService(accountClient, passphrase)
    await storeKeyInSession(dek)
    return dek
  }

  /**
   * Conversion step 1: ask Supabase to change the derived anonymous email to
   * the real one. The change is confirmed by email.
   */
  async function requestEmailChange(email: string, redirectTo?: string) {
    return requestAccountEmailChange(accountClient, email, redirectTo)
  }

  async function isEmailConfirmed(email: string) {
    return isEmailChangeConfirmed(accountClient, email)
  }

  /**
   * Conversion step 2: re-wrap the DEK currently in the session under the new
   * passphrase and clear the derived password so the account becomes
   * OTP-only. Content is not re-encrypted.
   */
  async function finalizeConversion(passphrase: string) {
    const { getKeyFromSession } = useEncryption()
    const dek = await getKeyFromSession()
    if (!dek) throw new Error('Encryption key not available')

    await rewrapDekForConversion(
      accountClient,
      await requireUserId(),
      dek,
      passphrase
    )
    const result = await finalizeAccountConversion(accountClient)

    if (!result.error) {
      const { clearLegacyKey } = useEncryption()
      const { clearCachedWrappedKey } = useKeyWrapping()
      clearLegacyKey()
      clearCachedWrappedKey()
    }

    return result
  }

  /**
   * Signs out the current user and clears the session DEK and legacy seed
   * material. The device passphrase key is intentionally kept in IndexedDB so
   * the user does not have to re-enter the passphrase on every login; it is
   * only requested on a new device or after storage is cleared (ADR-0007).
   * Sync hooks and realtime subscriptions are cleaned up reactively by
   * `useSync` when it detects the user has been set to null.
   */
  async function signOut() {
    const { clearEncryptionKey, clearLegacyKey } = useEncryption()
    const { clearCachedWrappedKey } = useKeyWrapping()
    clearEncryptionKey()
    clearLegacyKey()
    clearCachedWrappedKey()
    const { error } = await supabase.auth.signOut()
    if (error) throw error
  }

  /**
   * Deletes the user's cloud account and all associated remote data.
   *
   * Inserts a row into `deletion_requests` with the user's ID. A database
   * trigger (`handle_deletion_request`) runs server-side with elevated
   * privileges to delete all user data and the `auth.users` entry.
   *
   * After the request is submitted, local data and encryption key are
   * cleared and the user is signed out.
   */
  async function deleteAccount() {
    if (!user.value) throw new Error('No authenticated user')

    const { error } = await supabase
      .from('deletion_requests')
      .insert({ request: true } as never)

    if (error) throw error

    const { clearEncryptionKey, clearLegacyKey } = useEncryption()
    const { clearCachedWrappedKey } = useKeyWrapping()
    clearEncryptionKey()
    clearLegacyKey()
    clearCachedWrappedKey()
    await clearPassphraseKey()

    await supabase.auth.signOut()
  }

  return {
    user,
    authenticate,
    requestOtp,
    verifyOtp,
    isSeedAccount,
    setupNewAccount,
    unlockAccount,
    requestEmailChange,
    isEmailConfirmed,
    finalizeConversion,
    signOut,
    deleteAccount
  }
}
